import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ModernHeader from './ModernHeader';
import { useTranslation } from '../hooks/useTranslation';

import { asset } from '../utils/asset';

interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(true);
  const [splashOpacity, setSplashOpacity] = useState(1);
  const prevPathRef = useRef<string>('');

  // Určit, který obrázek použít podle sekce
  const getBackgroundImage = () => {
    const path = location.pathname;
    const search = location.search;

    // Pro celou sekci encyklopedie použít lapidar
    if (path.startsWith('/encyklopedie')) {
      return asset('/images/lapidar.png');
    }

    // Pro sekci kameny použít krystal
    if (search.includes('typ=kamen')) {
      return asset('/images/krystal.png');
    }

    // Pro sekci šperky použít kamenna panna
    if (search.includes('typ=sperk')) {
      return asset('/images/kamenna_panna_small.png');
    }

    // Pro sekci košík použít kosik
    if (path.startsWith('/kosik')) {
      return asset('/images/kosik.png');
    }
    
    // Pro všechny ostatní sekce použít logo
    return asset('/images/sperkarstvi_transparent_optimized.png');
  };

  // Splash screen efekt při změně stránky
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    // Zjistit, jestli jsme v encyklopedii
    const isInEncyclopedia = currentPath.startsWith('/encyklopedie');
    const wasInEncyclopedia = prevPath.startsWith('/encyklopedie');

    // Zobrazit splash screen pouze při:
    // 1. Vstupu do encyklopedie z jiné sekce
    // 2. Vstupu do jiné sekce z jakékoliv sekce
    // NEZOBRAZOVAT při pohybu uvnitř encyklopedie
    const shouldShowSplash = !(isInEncyclopedia && wasInEncyclopedia);

    if (shouldShowSplash) {
      // Reset splash screen při změně cesty
      setShowSplash(true);
      setSplashOpacity(1);

      // Po 0.5 sekundě začít fade out
      const fadeTimer = setTimeout(() => {
        setSplashOpacity(0);
      }, 500);

      // Po 3 sekundách (0.5s zobrazení + 2.5s fade) splash úplně skrýt
      const hideTimer = setTimeout(() => {
        setShowSplash(false);
      }, 3000);

      // Uložit aktuální cestu pro příští porovnání
      prevPathRef.current = currentPath;

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      // Pokud se pohybujeme uvnitř encyklopedie, pouze skrýt splash
      setShowSplash(false);
      prevPathRef.current = currentPath;
    }
  }, [location.pathname, location.search]);

  return (
    <div style={styles.container}>
      {/* Fixní pozadí s obrázkem kamene panny a krystalu */}
      <div style={{
        ...styles.backgroundImage,
        backgroundImage: `url(${getBackgroundImage()})`
      }}></div>

      {/* Splash screen - zobrazí se na 1 sekundu v popředí */}
      {showSplash && (
        <div style={{
          ...styles.splashScreen,
          backgroundImage: `url(${getBackgroundImage()})`,
          opacity: splashOpacity,
        }}></div>
      )}
      
      <ModernHeader />

      <main style={styles.main}>
        <div style={styles.content}>
          {children}
        </div>
      </main>

      {//TODO: je potreba pridat "Identifikační údaje a právní informace" - odkay na podminky, kontakty apod
      }
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p>&copy; 2025 ZR Sbírka - {t('footer.collectionBy')} {t('common.authorName')}</p>
          <p>{t('footer.viewingSystem')}</p>
          <p style={{ marginTop: '10px', fontSize: '0.9em', opacity: 0.8 }}>
            Web: Ing. Jindřich Kolář | <a href="mailto:jin.kolar@seznam.cz" style={{ color: 'inherit', textDecoration: 'underline' }}>jin.kolar@seznam.cz</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
  },
  backgroundImage: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    backgroundSize: 'auto 100vh', // Zvětšení na celou výšku okna
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    opacity: 0.35, // Průhlednost pro "zamlžený" efekt - zvýšeno o 20%
    pointerEvents: 'none' as const, // Neblokuje klikání na obsah
    zIndex: 0,
  },
  splashScreen: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    backgroundSize: 'auto 100vh', // Stejná velikost jako pozadí
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    pointerEvents: 'none' as const,
    zIndex: 9999, // Nad všemi ostatními elementy
    transition: 'opacity 2s ease-out', // Plynulý fade out efekt
  },
  main: {
    flex: 1,
    padding: '2rem 0',
    position: 'relative' as const,
    zIndex: 1,
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem',
  },
  footer: {
    backgroundColor: '#34495e',
    color: '#ecf0f1',
    padding: '1.5rem 0',
    marginTop: 'auto',
    position: 'relative' as const,
    zIndex: 0,
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem',
    textAlign: 'center' as const,
  },
};

export default PublicLayout;
